import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Form, Link, redirect, useActionData, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { ApiError, api } from '../lib/api';
import type { StudioIdentity } from '../lib/types';
import GoogleSignIn from '../components/GoogleSignIn';
import FormError from '../components/FormError';
import AuthScene from '../components/AuthScene';
import AuthWelcome from '../components/AuthWelcome';
import { captureSignupIntent, pricingPathFor, readSignupIntent } from '../lib/signupIntent';
import PasswordField from '../components/PasswordField';
import type { AuthState } from '../lib/types';

interface SignUpLoaderData {
  auth: AuthState;
}

interface SignUpActionData {
  error?: string;
}

const PERKS = [
  'Your Google name and picture become your crew card',
  'Film, VFX and campaigns on one production thread',
  'Every spend is quoted before anything renders'
];

export async function signUpLoader({ request }: LoaderFunctionArgs): Promise<SignUpLoaderData | Response> {
  const intent = captureSignupIntent(new URL(request.url));
  const auth = await api.authState();
  if (auth.authenticated) return redirect(intent ? pricingPathFor(intent) : '/');
  return { auth };
}

export async function signUpAction({ request }: ActionFunctionArgs): Promise<SignUpActionData | Response> {
  const form = await request.formData();
  try {
    await api.bootstrap({
      name: String(form.get('name') || ''),
      studioName: String(form.get('studioName') || ''),
      email: String(form.get('email') || ''),
      password: String(form.get('password') || '')
    });
    return redirect('/welcome');
  } catch (error) {
    return { error: error instanceof ApiError || error instanceof Error ? error.message : 'Could not create the studio.' };
  }
}

export default function SignUp() {
  const { auth } = useLoaderData() as SignUpLoaderData;
  const actionData = useActionData() as SignUpActionData | undefined;
  const navigation = useNavigation();
  const [identity, setIdentity] = useState<StudioIdentity | null>(null);
  const [created, setCreated] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const busy = navigation.state !== 'idle';
  const [intent] = useState(readSignupIntent);
  const setup = auth.needsSetup;
  const ownStudio = setup || Boolean(auth.privateStudios);

  useEffect(() => {
    if (actionData?.error) setFormOpen(true);
  }, [actionData]);

  const scene = ownStudio
    ? {
        eyebrow: 'First light',
        headline: ['Open the', 'studio.'],
        ghost: 'Roll camera.',
        lede: 'Everything downstream — projects, media, provider keys and spend — lives inside the studio you are about to create.'
      }
    : {
        eyebrow: 'Join the floor',
        headline: ['Ten artists.', 'One floor.'],
        ghost: 'One thread.',
        lede: 'Story, world, coverage and master move through a single production thread. Your crew sees the same slate you do.'
      };

  if (identity) {
    return (
      <AuthScene {...scene}>
        <AuthWelcome identity={identity} next={created ? '/welcome' : intent ? pricingPathFor(intent) : '/'} greeting={created ? (ownStudio ? 'Your studio is live' : 'Welcome aboard') : 'Welcome back'} />
      </AuthScene>
    );
  }

  return (
    <AuthScene {...scene}>
      <div className="sa-view">
        <span className="eyebrow">{setup ? 'First-time setup' : ownStudio ? 'New studio' : 'Join the studio'}</span>
        <h1>{ownStudio ? 'Create your private studio' : 'Create your account'}</h1>
        <p className="auth-sub">
          {ownStudio
            ? 'Sign up with Google and you become the studio owner. You will name the studio right after.'
            : 'One click with Google and you are on the call sheet. An owner can raise your role from team settings.'}
        </p>

        <GoogleSignIn
          mode="signup"
          autoPrompt={false}
          onSignedIn={(value, isNew) => {
            setGoogleError('');
            setCreated(isNew);
            setIdentity(value);
          }}
          onError={setGoogleError}
        />
        {googleError ? (
          <div className="sa-error-slot">
            <FormError message={googleError} />
          </div>
        ) : null}

        <ul className="sa-perks">
          {PERKS.map((perk, index) => (
            <li key={perk} style={{ '--i': index } as CSSProperties}>
              {perk}
            </li>
          ))}
        </ul>

        {setup ? (
          <div className="sa-disclosure">
            <button type="button" className="sa-toggle" aria-expanded={formOpen} aria-controls="signup-email" onClick={() => setFormOpen(open => !open)}>
              <span>
                Use email instead <i aria-hidden="true">+</i>
              </span>
            </button>
            <div id="signup-email" className="sa-collapse" data-open={formOpen || undefined}>
              <div>
                <Form method="post" className="auth-form" replace inert={!formOpen || undefined}>
                  <FormError message={actionData?.error} />
                  <label className="field">
                    <span>Your name</span>
                    <input className="input" name="name" required autoComplete="name" />
                  </label>
                  <label className="field">
                    <span>Email</span>
                    <input className="input" type="email" name="email" required autoComplete="email" />
                  </label>
                  <PasswordField label="Password" name="password" autoComplete="new-password" minLength={10} hint="At least 10 characters." meter />
                  <button className="app-button primary" disabled={busy}>
                    {busy ? 'Creating…' : 'Create secure studio'}
                  </button>
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <p className="sa-mono sa-fine">Password accounts are created by invitation.</p>
        )}

        <p className="auth-alt sa-footer">
          Already have an account? <Link to="/signin">Sign in</Link>
        </p>
      </div>
    </AuthScene>
  );
}
